import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useContent } from "../lib/content";
import { MapPin, ShieldCheck, Phone } from "lucide-react";
import { toast } from "sonner";

export default function Product() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState("");
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const { content: c } = useContent();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActiveImg(r.data.image);
    });
  }, [id]);

  if (!product) {
    return (
      <div className="px-6 py-24 text-center text-[var(--srishti-text-secondary)]">
        Loading…
      </div>
    );
  }

  const telHref = `tel:${(c.store_phone || "").replace(/\s+/g, "")}`;
  const allImages = [product.image, ...(product.images || []).filter((x) => x && x !== product.image)];

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 md:py-16 fade-up" data-testid="product-page">
      <div className="max-w-[1400px] mx-auto">
        <Link to="/shop" className="text-xs uppercase tracking-widest text-[var(--srishti-text-secondary)] hover:text-[var(--srishti-maroon)]">
          ← Back to shop
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 mt-6">
          <div>
            <div className="image-zoom aspect-[4/5] bg-[var(--srishti-bg-alt)] overflow-hidden">
              <img
                src={activeImg || product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
            {allImages.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2" data-testid="product-thumbnails">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImg(img)}
                    className={`aspect-square overflow-hidden border-2 transition-colors ${
                      (activeImg || product.image) === img
                        ? "border-[var(--srishti-gold)]"
                        : "border-transparent hover:border-[var(--srishti-border)]"
                    }`}
                    data-testid={`product-thumbnail-${i}`}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            {product.badge && (
              <span className="eyebrow text-[var(--srishti-saffron)]">
                {product.badge}
              </span>
            )}
            <h1 className="font-serif-display text-4xl md:text-5xl mt-2 leading-tight" data-testid="product-name">
              {product.name}
            </h1>

            {/* CALL FOR PRICE CARD */}
            <a
              href={telHref}
              className="mt-6 flex items-center gap-4 bg-[var(--srishti-bg-alt)] border border-[var(--srishti-border)] hover:border-[var(--srishti-gold)] transition-colors p-4 group"
              data-testid="call-for-price-card"
            >
              <div className="w-11 h-11 rounded-full bg-[var(--srishti-maroon)] text-[#FDFBF7] flex items-center justify-center shrink-0">
                <Phone size={18} />
              </div>
              <div>
                <p className="eyebrow text-[var(--srishti-saffron)]">Call for price</p>
                <p className="font-serif-display text-2xl text-[var(--srishti-maroon)] leading-tight">
                  {c.store_phone}
                </p>
              </div>
            </a>

            <p className="mt-6 text-[var(--srishti-text-secondary)] leading-relaxed">
              {product.description}
            </p>

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-[var(--srishti-border)]">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-3 py-2"
                  data-testid="qty-decrease"
                >
                  −
                </button>
                <span className="px-4 text-sm">{qty}</span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="px-3 py-2"
                  data-testid="qty-increase"
                >
                  +
                </button>
              </div>
              <button
                className="btn-maroon flex-1"
                onClick={() => {
                  addItem(product, qty);
                  toast.success("Added to enquiry list");
                }}
                disabled={!product.in_stock}
                data-testid="add-to-cart-button"
              >
                {product.in_stock ? "Add to Enquiry" : "Sold Out"}
              </button>
            </div>

            <ul className="mt-10 space-y-4 border-t border-[var(--srishti-border)] pt-6 text-sm">
              <li className="flex gap-3">
                <MapPin size={18} className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" />
                <span>
                  <span className="font-semibold">Local pickup</span> at our TC Palya, Bangalore boutique
                </span>
              </li>
              <li className="flex gap-3">
                <Phone size={18} className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" />
                <span>Call <span className="font-semibold">{c.store_phone}</span> for price &amp; availability</span>
              </li>
              <li className="flex gap-3">
                <ShieldCheck size={18} className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" />
                <span>Hand-checked by our curators before handover</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
